import { useEffect, useRef } from 'react';

/**
 * The platform's "dismiss" key — `Escape` on web, the Android hardware Back
 * button on native — routed to the TOPMOST open sheet only.
 *
 * Every open sheet claims the key; the last claim wins, and releasing it hands
 * the key back to the claim underneath, so the key walks a stack of sheets down
 * one level per press instead of collapsing it all at once (or, on Android,
 * skipping the sheets entirely and popping the screen behind them).
 *
 * Platform-free on purpose: each platform file supplies only `attach` — how to
 * listen for its key — and the stack, ordering and release rules are this one
 * definition.
 */
export interface DismissKeyStack {
  /**
   * Take the key until the returned release runs. Releasing is idempotent and
   * order-independent: a sheet BELOW the top can close first (its host flips
   * `open` directly) without disturbing the claims above it.
   */
  claim(onDismissKey: () => void): () => void;
  /** Open claims — for tests and diagnostics. */
  size(): number;
}

/**
 * @param attach Start listening for the key; call `dispatch` when it is
 *   pressed (it returns whether a claim consumed it) and return a detach.
 *   Attached while at least one claim is held — and re-attached on EVERY new
 *   claim, so a listener another screen registered after the first sheet
 *   opened (Android's BackHandler runs the most recent subscriber first) can
 *   never sit in front of the sheet the user just opened.
 */
export function createDismissKeyStack(
  attach: (dispatch: () => boolean) => () => void,
): DismissKeyStack {
  const claims: { onDismissKey: () => void }[] = [];
  let detach: (() => void) | null = null;

  const dispatch = () => {
    const top = claims[claims.length - 1];
    if (!top) return false;
    top.onDismissKey();
    return true;
  };

  return {
    claim(onDismissKey) {
      const entry = { onDismissKey };
      claims.push(entry);
      detach?.();
      detach = attach(dispatch);
      return () => {
        const index = claims.indexOf(entry);
        if (index === -1) return;
        claims.splice(index, 1);
        if (claims.length === 0) {
          detach?.();
          detach = null;
        }
      };
    },
    size: () => claims.length,
  };
}

/**
 * Hold `stack`'s key while `active`, calling the LATEST `onDismissKey` on each
 * press. The claim is keyed on `active` alone, so a fresh callback every
 * render never re-claims — a re-claim would move a lower sheet back on top.
 */
export function useDismissKeyClaim(
  stack: DismissKeyStack,
  active: boolean,
  onDismissKey: () => void,
) {
  const latest = useRef(onDismissKey);
  useEffect(() => {
    latest.current = onDismissKey;
  });
  useEffect(() => {
    if (!active) return;
    return stack.claim(() => latest.current());
  }, [stack, active]);
}
